import { CommonTrackingConfig } from '../../../modules/utils/trackingHelper';

import type { WalletAwardsModalsSlice } from './modals';

export type {
  WalletAwardModalId,
  WalletAwardModalInstanceKey,
  WalletAwardModalLayout,
  WalletAwardInstructionStep,
  WalletAwardsModalsShared,
  WalletAwardsModalsLayouts,
  WalletAwardModalInstance,
  WalletAwardsModalsSlice,
  ResolvedWalletModalContent,
  AemModalSlice,
  AemAwardKey,
  AemDetailStateKey,
  AemModalSource,
} from './modals';
export {
  WALLET_AWARD_MODAL_ID,
  WALLET_AWARD_MODAL_INSTANCE,
  WALLET_AWARD_MODAL_ID_TO_INSTANCE,
  WALLET_AWARD_STATUS,
  resolveAemModalSource,
} from './modals';

export type WalletFnaItemStatus = 'available' | 'applied';

export enum WalletNightlyUpgradeAwardsSectionStatus {
  Available = 'available',
  Approved = 'approved',
  RequestPending = 'requestPending',
}
export type WalletNuaExpiryGroup = {
  count: number;
  expirationDate: string;
};

export type WalletFnaItem = {
  id: string;
  awardCode?: string;
  awardDescription?: string;
  expirationDate?: string;
  status: WalletFnaItemStatus;
  propertyId?: string;
  propertyName?: string;
  propertyImageSrc?: string;
  propertyImageAltText?: string;
  numberOfNights?: number;
  isCancellable?: boolean;
  checkInDate?: string;
  checkOutDate?: string;
  confirmationNumber?: string;
};

export type WalletNuaItem = {
  id: string;
  count: number;
  expirationDate?: string;
  expiryGroups?: WalletNuaExpiryGroup[];
  status: WalletNightlyUpgradeAwardsSectionStatus;
  propertyId?: string;
  propertyName?: string;
  propertyImageSrc?: string;
  propertyImageAltText?: string;
  checkInDate?: string;
  checkOutDate?: string;
  confirmationNumber?: string;
};

export interface AEMTierImage {
  memberCode?: string | string[];
  image?: string;
  imageAltText?: string;
  featuredDynamicImage?: AEMAwardsEmptyStateImage;
}

export interface EmptyStateCardModel {
  awardType: string;
  iconPath: string;
  iconAltText?: string;
  title: string;
  description: string;
  ctaLabel: string;
  ctaAriaLabel?: string;
  ctaPath?: string;
  ctaAction?: string;
  ctaModalId?: string;
  openInNewTab?: boolean;
}

export interface RawEmptyStateCardModel {
  awardType?: string;
  iconPath?: string;
  iconAltText?: string;
  title?: string;
  description?: string;
  ctaLabel?: string;
  ctaAriaLabel?: string;
  ctaPath?: string;
  ctaAction?: string;
  ctaModalId?: string;
  openInNewTab?: boolean;
  tracking?: {
    keyValue?: string;
  };
}

export interface AEMAwardsCommon {
  defaultLabel?: string;
  availablePluralLabel?: string;
  availableSingularLabel?: string;
  expiresLabel?: string;
  expiresSingularLabel?: string;
  availableLabel?: string;
  appliedLabel?: string;
  approvedLabel?: string;
  requestPendingLabel?: string;

  appliedStatusIcon?: string;
  approvedStatusIcon?: string;
  requestPendingStatusIcon?: string;

  closeLabel?: string;

  fnaImages?: AEMTierImage[];
  nuaImages?: AEMTierImage[];

  awardExpiration?: string;
  checkInLabel?: string;
  checkOutLabel?: string;
  confirmationLabel?: string;
  propertyNameLabel?: string;
  singleExpireLabel?: string;
  expiringLabel?: string;
  expireDLabel?: string;
  expiringSoonDays?: string | number;
}

export interface AEMAwardsBanner {
  annualChoiceBenefitHeading?: string;
  annualChoiceBenefitDescription?: string;
  annualChoiceBenefitCtaLabel?: string;
  annualChoiceBenefitCtaLink?: string;
  annualChoiceBenefitCtaOpenInANewTab?: boolean;
  annualChoiceBenefitExpiryDate?: string;
  annualChoiceBenefitIcon?: string;
  tracking?: CommonTrackingConfig;
}

export interface AEMFreeNightAwardsSection {
  sectionHeading?: string;
  description?: string;
  descriptionLinkLabel?: string;
  descriptionLink?: string;
  descriptionLinkOpenInANewTab?: boolean;
  tracking?: CommonTrackingConfig;
  freeNightAwardIcon?: string;
}

export interface AEMNightlyUpgradeAwardsSection {
  sectionHeading?: string;
  description?: string;
  descriptionLinkLabel?: string;
  descriptionLink?: string;
  descriptionLinkOpenInANewTab?: boolean;
  nightlyUpgradeAwardIcon?: string;
  tracking?: CommonTrackingConfig;
}

export interface AEMHowToEarnModal {
  key?: string;
  modalTitle?: string;
  contentTitle?: string;
  contentDescription?: string;
  bodyContentHeader?: string;
  bodyContent?: string;
  ctaLinkLabel?: string;
  ctaLink?: string;
  ctaLinkOpenInANewTab?: boolean;
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  tracking?: CommonTrackingConfig;
}

export interface AEMDetailState {
  key?: string;
  copy?: string;
  title?: string;
  disclaimer?: string;
  ctaLinkLabel?: string;
  ctaLinkPath?: string;
  ctaLinkOpenInANewTab?: boolean;
  showReservationDetails?: boolean;
  tracking?: CommonTrackingConfig;
}

export interface AEMInstructionStepOrTip {
  icon?: string;
  text?: string;
  body?: string;
}

export interface AEMTipsToggle {
  label?: string;
  ariaLabel?: string;
  initiallyExpanded?: boolean;
  tipsList?: AEMInstructionStepOrTip[];
  tracking?: {
    toggleOpenValue: string;
    toggleClosedValue: string;
  };
}

export interface AEMDetailModal {
  key?: string;
  modalTitle?: string;
  awardCountSingularLabel?: string;
  awardCountPluralLabel?: string;
  instructionSteps?: AEMInstructionStepOrTip[];
  tipsToggle?: AEMTipsToggle;
  states?: AEMDetailState[];
}

export interface AEMAwardsEmptyStateImage {
  damPath?: string;
  assetPath?: string;
  altText?: string;
  dynamic?: boolean;
  enableSmartCrop?: boolean;
  renditions?: unknown[];
  renditionUrl?: string | null;
}

export interface AEMAwardsEmptyState {
  freeNightEmptyHeading?: string;
  freeNightEmptyMessage?: string;
  freeNightEmptyImage?: string;
  freeNightEmptyImageAltText?: string;
  freeNightEmptyAltText?: string;
  freeNightEmptyDynamicImage?: AEMAwardsEmptyStateImage;
  freeNightEmptyLinkLabel?: string;
  freeNightEmptyLink?: string;
  freeNightEmptyLinkOpenInANewTab?: boolean;
  nightlyUpgradeEmptyHeading?: string;
  nightlyUpgradeEmptyMessage?: string;
  nightlyUpgradeEmptyImage?: string;
  nightlyUpgradeEmptyImageAltText?: string;
  nightlyUpgradeEmptyAltText?: string;
  nightlyUpgradeEmptyDynamicImage?: AEMAwardsEmptyStateImage;
  nightlyUpgradeEmptyLinkLabel?: string;
  nightlyUpgradeEmptyLink?: string;
  nightlyUpgradeEmptyLinkOpenInANewTab?: boolean;
  tracking?: CommonTrackingConfig;
}

export interface WalletAwardsSectionModel {
  ':type'?: string;
  componentId?: string;
  hideComponent?: boolean;
  customClass?: string;
  common?: AEMAwardsCommon;
  banner?: AEMAwardsBanner;
  freeNight?: AEMFreeNightAwardsSection;
  nightlyUpgrade?: AEMNightlyUpgradeAwardsSection;
  emptyState?: AEMAwardsEmptyState;
  howtoEarn?: AEMHowToEarnModal[];
  details?: AEMDetailModal[];
  modals?: WalletAwardsModalsSlice;
}

/**
 * Props for the WalletAwardsSection orchestrator component.
 */
export interface WalletAwardsSectionProps {
  isAuthorMode?: boolean;
  model: WalletAwardsSectionModel;
  // AEM-provided props
  cqItems?: object;
  itemPath?: string;
  pagePath?: string;
}

export interface WalletAwardsBannerProps {
  banner?: AEMAwardsBanner;
  memberIconColor?: string;
  locale?: string;
}

export interface WalletFreeNightAwardsProps {
  common?: AEMAwardsCommon;
  freeNight?: AEMFreeNightAwardsSection;
  emptyState?: AEMAwardsEmptyState;
  items: WalletFnaItem[];
  memberIconColor?: string;
  currentLocale: string;
  onHowToEarnClick?: () => void;
  onAwardTileClick?: (item: WalletFnaItem) => void;
}

export interface WalletNightlyUpgradeAwardsProps {
  common?: AEMAwardsCommon;
  nightlyUpgrade?: AEMNightlyUpgradeAwardsSection;
  emptyState?: AEMAwardsEmptyState;
  items: WalletNuaItem[];
  memberIconColor?: string;
  currentLocale: string;
  onHowToEarnClick?: () => void;
  onAwardTileClick?: (item: WalletNuaItem) => void;
}

export interface NightlyUpgradeAwardDetail {
  count: number;
  details?: {
    expirationDate: string;
    count: number;
  }[];
}

export interface NightlyUpgradeAwards {
  available?: NightlyUpgradeAwardDetail | NightlyUpgradeAwardDetail[];
  expired?: NightlyUpgradeAwardDetail | NightlyUpgradeAwardDetail[];
}

export interface CertificateNode {
  awardType?: {
    code: string;
    label: string;
    description: string;
    enumCode: string;
  };
  expirationDate?: string;
  isCancellable?: boolean;
  numberOfNights?: number;
  points?: number;
}

export interface Certificates {
  total?: number;
  edges?: {
    node: CertificateNode;
  }[];
  status?: {
    code?: string;
    httpStatus?: number;
  };
}

export interface RewardsEarnedData {
  customer?: {
    loyaltyInformation?: {
      nightlyUpgradeAwards?: NightlyUpgradeAwards;
      certificates?: Certificates;
    };
  };
}
